import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUserStore } from "../../store/userStore";
import { useAvisosStore, type Aviso } from "../../store/admin/avisosStore";
import ImagePreviewModal from "./ImagePreviewModal";
import { isSandboxView } from "../../features/sandbox/sandboxView";

export default function AvisosLoginModal() {
  const { t } = useTranslation("common");
  const user = useUserStore((state) => state.user);
  const fetchActiveAvisos = useAvisosStore((s) => s.fetchActiveAvisos);
  const markAvisoAsRead = useAvisosStore((s) => s.markAvisoAsRead);

  const [queue, setQueue] = useState<Aviso[]>([]);
  const [index, setIndex] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    // Los avisos de ejemplo no se pueden marcar como leídos: el tenant es de solo
    // lectura y volverían a tapar la pantalla en cada carga. Siguen en su sección.
    if (!user || isSandboxView()) {
      setQueue([]);
      setIndex(0);
      return;
    }

    let cancelled = false;
    fetchActiveAvisos().then((avisos) => {
      if (cancelled) return;
      setQueue(avisos || []);
      setIndex(0);
    });

    return () => {
      cancelled = true;
    };
  }, [user, fetchActiveAvisos]);

  if (!user || queue.length === 0 || index >= queue.length) return null;

  const current = queue[index];

  const close = () => {
    markAvisoAsRead(current.id);
    if (index + 1 < queue.length) {
      setIndex(index + 1);
    } else {
      setQueue([]);
      setIndex(0);
    }
  };

  const cardBg = "bg-card";
  const text = "text-content";
  const textSecondary = "text-content-secondary";
  const borderColor = "border-line";
  const dividerColor = "border-line";

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4">
        <div
          className={`${cardBg} ${text} rounded-2xl shadow-2xl w-[75vw] h-[75vh] max-w-none flex flex-col overflow-hidden border ${borderColor}`}
        >
          {current.image && (
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="relative group w-full flex-shrink-0 h-[55%] overflow-hidden focus:outline-none"
              aria-label={t("avisosModal.enlargeImage")}
            >
              <img
                src={current.image}
                alt={current.title || t("avisosModal.notice")}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
              />
              <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors pointer-events-none">
                <i className="ri-zoom-in-line text-white text-4xl opacity-0 group-hover:opacity-100 transition-opacity"></i>
              </span>
            </button>
          )}

          <div className="flex-1 flex flex-col p-6 min-h-0">
            <div className="flex-1 overflow-y-auto pr-1">
              {current.title && (
                <h2 className="text-2xl font-bold mb-3">{current.title}</h2>
              )}
              {current.description && (
                <p className={`${textSecondary} whitespace-pre-line text-sm leading-relaxed`}>
                  {current.description}
                </p>
              )}
            </div>

            <div
              className={`flex items-center justify-between mt-4 pt-4 flex-shrink-0 border-t ${dividerColor}`}
            >
              <span className={`text-xs ${textSecondary}`}>
                {queue.length > 1
                  ? t("avisosModal.noticeOf", {
                      current: index + 1,
                      total: queue.length,
                    })
                  : ""}
              </span>
              <button
                type="button"
                onClick={close}
                className="bg-primary-500 hover:bg-primary-400 text-on-primary px-6 py-2.5 rounded-lg font-medium transition-colors"
              >
                {index + 1 < queue.length
                  ? t("avisosModal.next")
                  : t("avisosModal.understood")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {previewOpen && current.image && (
        <ImagePreviewModal
          src={current.image}
          alt={current.title || t("avisosModal.notice")}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </>
  );
}
